/**
 * pilotOversightApi — Risk panel > Reviews > Pilot oversight (Risk panel 0.27.0, pcb-retail-api 3.48.0, Bill 2026-11-04).
 *
 *   GET   /v3/risk/pilot/board[?retired=true]          every section (incl. Risk-only), items, counts, issues
 *   GET   /v3/risk/pilot/history[?top=]                 the latest changes everywhere: who updated what
 *   GET   /v3/risk/pilot/items/{id}/history
 *   PATCH /v3/risk/pilot/items/{id}                     any field, plus retired
 *   POST  /v3/risk/pilot/sections/{id}/items            add an item
 *   POST  /v3/risk/pilot/sections                       add a section
 *   PATCH /v3/risk/pilot/sections/{id}                  change or retire a section
 *
 * RiskManager (SuperAdmin passes) and a verified Entra sign-in; the server records the signed-in person on every
 * change, never anything sent here. Bearer only; no function key in the bundle.
 *
 * Sources (CLAUDE.md rule 2): every field is a column of dbo.PilotTestSections / PilotTestItems / PilotTestHistory /
 * PilotTestIssues (migration 20261104180000_V3_PilotTesting), loaded from the pilot test workbook by seed 456.
 */
import { buildHeaders, getBaseUrl } from './riskSettingsApi';

export type PilotKind = 'checklist' | 'scenarios' | 'results';

export interface PilotCounts {
  notStarted: number;
  inProgress: number;
  complete: number;
  issueIdentified: number;
  notApplicable: number;
  total: number;
  percentComplete: number;
  pass: number;
  fail: number;
}

export interface PilotSection {
  id: number;
  code: string;
  name: string;
  kind: PilotKind;
  audience: 'testers' | 'risk';
  phase: string;
  phaseNote: string | null;
  description: string | null;
  columnLabels: Record<string, string>;
  testersMayAdd: boolean;
  canAdd: boolean;
  retired: boolean;
  sortOrder: number;
  counts: PilotCounts;
}

export interface PilotIssue {
  reportId: string;
  changeKey: string | null;
  reportedBy: string;
  reportedAt: string;
}

export interface PilotItem {
  id: number;
  sectionId: number;
  code: string | null;
  group: string | null;
  area: string | null;
  title: string | null;
  steps: string | null;
  channel: string | null;
  testerGroup: string | null;
  accountTypes: string | null;
  riskRole: string | null;
  status: string | null;
  result: string | null;
  assignedTo: string | null;
  assignedToUpn: string | null;
  dueDate: string | null;
  evidenceRef: string | null;
  notes: string | null;
  details: Array<{ label: string; value: string | null }>;
  source: string;
  retired: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  version: string;
  issues: PilotIssue[];
}

export interface PilotBoard {
  canManage: boolean;
  me: { upn: string; name: string | null };
  statuses: string[];
  sections: PilotSection[];
  items: PilotItem[];
}

export interface PilotHistoryRow {
  id: number;
  sectionId: number;
  sectionName: string | null;
  itemId: number | null;
  itemCode: string | null;
  itemTitle: string | null;
  action: string;
  field: string | null;
  fieldLabel: string | null;
  oldValue: string | null;
  newValue: string | null;
  byUpn: string;
  byName: string | null;
  at: string;
  via: string;
}

/** Any item field Risk may change; omitted = unchanged, "" = clear. */
export type PilotItemPatch = Partial<Record<
  'status' | 'result' | 'notes' | 'assignedTo' | 'dueDate' | 'evidenceRef' | 'itemCode' | 'groupName' | 'area' | 'title'
  | 'steps' | 'channel' | 'testerGroup' | 'accountTypes' | 'riskRole', string>> & { retired?: boolean; assignToMe?: boolean; expectedVersion?: string };

export interface PilotSectionInput {
  name?: string;
  kind?: PilotKind;
  audience?: 'testers' | 'risk';
  phase?: string;
  phaseNote?: string;
  description?: string;
  testersMayAdd?: boolean;
  retired?: boolean;
}

export class PilotApiError extends Error {
  constructor(message: string, readonly code: string | null, readonly status: number) {
    super(message);
    this.name = 'PilotApiError';
  }
}

interface Envelope { success?: boolean; error?: string; code?: string }

async function read<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: (T & Envelope) | null = null;
  try { body = text ? (JSON.parse(text) as T & Envelope) : null; } catch { body = null; }
  if (!resp.ok || (body && body.success === false)) {
    const fallback = resp.status === 403
      ? 'Pilot oversight is for the Risk Management team (RiskManager role).'
      : `The server answered ${resp.status}.`;
    throw new PilotApiError(body?.error ?? fallback, body?.code ?? null, resp.status);
  }
  if (!body) throw new PilotApiError('Empty response from the server.', null, resp.status);
  return body;
}

const base = (): string => `${getBaseUrl()}/v3/risk/pilot`;

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await fetch(`${base()}${path}`, {
    method,
    headers: await buildHeaders(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  return read<T>(resp);
}

export const loadPilotBoard = (retired: boolean): Promise<PilotBoard> => call('GET', `/board${retired ? '?retired=true' : ''}`);
export const loadPilotHistory = (top = 200): Promise<{ history: PilotHistoryRow[] }> => call('GET', `/history?top=${top}`);
export const loadItemHistory = (id: number): Promise<{ history: PilotHistoryRow[] }> => call('GET', `/items/${id}/history`);
export const updatePilotItem = (id: number, patch: PilotItemPatch): Promise<{ item: PilotItem; changed: number }> => call('PATCH', `/items/${id}`, patch);
export const addPilotItem = (sectionId: number, input: Record<string, string>): Promise<{ item: PilotItem }> => call('POST', `/sections/${sectionId}/items`, input);
export const addPilotSection = (input: PilotSectionInput): Promise<{ section: { id: number; name: string } }> => call('POST', '/sections', input);
export const updatePilotSection = (id: number, input: PilotSectionInput): Promise<{ changed: number }> => call('PATCH', `/sections/${id}`, input);
